import type { FrontRoomsView, FrontRoomView } from '@obraplan/core/application/contracts/takeoff';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  aptosDaFrente,
  avancoDosComodos,
  chaveDoComodo,
  estadoDoComodo,
  LEGENDA_DO_COMODO,
  nomeDoApto,
  percentual,
  type AptoDaFrente,
  type EstadoDoComodo,
} from './planta-pintada';
import { lerPercentual, type RascunhoDosComodos } from './rascunho-dos-comodos';

/**
 * Os aptos × cômodos de uma frente para um serviço (Fase 34, etapa 2 — RN-441 a RN-445).
 *
 * Duas formas, o mesmo desenho:
 *
 * - **só leitura** (o item do cronograma, a tela de execução): a grade de ladrilhos, um por
 *   cômodo, pintado até o percentual que vale (redesenho de 21/10/2026) — e o apto com o
 *   avanço dele, pesado como o do item;
 * - **editável** (o apontamento do dia, D7): um campo de 0 a 100 por cômodo e a caixa do "não se
 *   aplica". A caixa é do cômodo da **tipologia** (RN-444): marcá-la num apto marca em todos os
 *   aptos dela, e a tela diz isso.
 *
 * É controlada: quem grava é o pai, ao salvar o dia, com o rascunho que recebe de volta.
 */
export interface ComodosDaFrenteProps {
  readonly view: FrontRoomsView;
  /** A unidade do serviço, para a medida de cada cômodo ("m²"). */
  readonly unit?: string | undefined;
  /** Com rascunho, a lista é editável. */
  readonly rascunho?: RascunhoDosComodos;
  readonly onChange?: (rascunho: RascunhoDosComodos) => void;
  readonly disabled?: boolean;
  readonly className?: string;
  /**
   * Só leitura com escolha (23/10/2026): cada ladrilho vira botão, e o pai marca os escolhidos
   * pelo mesmo painel da tela cheia. As chaves são as de `chaveDoComodo`.
   */
  readonly selected?: ReadonlySet<string>;
  readonly onToggleRoom?: (room: FrontRoomView) => void;
}

function diaEMes(data: string): string {
  const [, mes, dia] = data.split('-');
  return mes === undefined || dia === undefined ? data : `${dia}/${mes}`;
}

function medida(room: FrontRoomView, unit: string | undefined): string | undefined {
  if (room.measure === undefined) return undefined;
  const numero = room.measure.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return unit === undefined ? numero : `${numero} ${unit}`;
}

export function ComodosDaFrente({
  view,
  unit,
  rascunho,
  onChange,
  disabled = false,
  className,
  selected,
  onToggleRoom,
}: ComodosDaFrenteProps) {
  const aptos = aptosDaFrente(view.rooms);
  const editavel = rascunho !== undefined && onChange !== undefined;

  if (!editavel) {
    return (
      <GradeDeComodos
        view={view}
        aptos={aptos}
        unit={unit}
        className={className}
        selected={selected}
        onToggleRoom={onToggleRoom}
      />
    );
  }

  // Quantos aptos têm cada cômodo da tipologia — o aviso de que a marca vale para todos.
  const aptosDoComodo = new Map<string, number>();
  for (const room of view.rooms) {
    aptosDoComodo.set(room.roomId, (aptosDoComodo.get(room.roomId) ?? 0) + 1);
  }
  const naoSeAplica = (room: FrontRoomView): boolean =>
    rascunho.naoSeAplica[room.roomId] ?? room.notApplicable;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <AvisoDoPeso view={view} />
      <div className="flex max-h-[26rem] flex-col gap-3 overflow-y-auto pr-1">
        {aptos.map((apto) => (
          <section
            key={apto.positionId}
            aria-label={nomeDoApto(apto.label)}
            className="rounded-md border border-(--color-border-subtle)"
          >
            <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-(--color-border-subtle) bg-(--color-surface-muted) px-3 py-1.5">
              <h4 className="text-sm font-medium">
                {nomeDoApto(apto.label)}
                {apto.count > 1 && (
                  <span className="font-normal text-(--color-content-muted)">
                    {' '}
                    · {String(apto.count)} iguais
                  </span>
                )}
              </h4>
              <span className="text-xs text-(--color-content-muted)">{apto.unitTypeName}</span>
            </header>
            <ul className="divide-y divide-(--color-border-subtle)">
              {apto.rooms.map((room) => {
                const chave = chaveDoComodo(room);
                const fora = naoSeAplica(room);
                const texto = rascunho.percentuais[chave];
                const invalido = texto !== undefined && lerPercentual(texto) === null;
                const nome = `${room.roomName} · ${nomeDoApto(apto.label)}`;
                const medidaDoComodo = medida(room, unit);
                return (
                  <li
                    key={chave}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5"
                  >
                    <span
                      className={cn('text-sm', fora && 'text-(--color-content-muted) line-through')}
                    >
                      {room.roomName}
                      {medidaDoComodo !== undefined && (
                        <span className="text-xs text-(--color-content-muted) no-underline">
                          {' · '}
                          {medidaDoComodo}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={fora}
                          disabled={disabled}
                          title={
                            (aptosDoComodo.get(room.roomId) ?? 1) > 1
                              ? `Vale para ${room.roomName} de todos os aptos da tipologia ${apto.unitTypeName}`
                              : undefined
                          }
                          onChange={(event) =>
                            onChange({
                              ...rascunho,
                              naoSeAplica: {
                                ...rascunho.naoSeAplica,
                                [room.roomId]: event.target.checked,
                              },
                            })
                          }
                        />
                        não se aplica
                      </label>
                      <span className="flex items-center gap-1">
                        <Input
                          aria-label={`Percentual de ${nome}`}
                          inputMode="decimal"
                          className={cn(
                            'h-9 w-20 text-right tabular',
                            invalido && 'border-(--color-state-danger)',
                          )}
                          placeholder={room.percent === undefined ? '0' : undefined}
                          value={
                            texto ??
                            (room.percent === undefined
                              ? ''
                              : String(room.percent).replace('.', ','))
                          }
                          disabled={disabled || fora}
                          aria-invalid={invalido || undefined}
                          onChange={(event) =>
                            onChange({
                              ...rascunho,
                              percentuais: {
                                ...rascunho.percentuais,
                                [chave]: event.target.value,
                              },
                            })
                          }
                        />
                        <span className="text-sm text-(--color-content-muted)">%</span>
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      <p className="text-xs text-(--color-content-muted)">
        O percentual é o avanço físico do cômodo, e não entra na quantidade do dia. &ldquo;Não se
        aplica&rdquo; vale para o cômodo em todos os aptos da mesma tipologia.
      </p>
    </div>
  );
}

function AvisoDoPeso({ view }: { readonly view: FrontRoomsView }) {
  if (view.basis !== 'IGUAL') return null;
  return (
    <p role="note" className="text-xs text-(--color-state-warn)">
      O desenho não mede este serviço por cômodo: no avanço do item, todos os cômodos pesam igual.
    </p>
  );
}

/** A classe do ladrilho em cada estado: a borda diz o estado; a pintura, o quanto. */
const BORDA_DO_ESTADO: Readonly<Record<EstadoDoComodo, string>> = {
  NAO_INICIADO: 'border-(--color-content-muted)',
  EM_ANDAMENTO: 'border-(--color-brand)',
  CONCLUIDO: 'border-(--color-state-ok)',
  NAO_SE_APLICA: 'border-dashed border-(--color-state-neutral) text-(--color-content-muted)',
};

/**
 * A grade de ladrilhos: um por cômodo, pintado da esquerda até o percentual que vale — azul em
 * andamento, verde concluído —, agrupados por apto com o avanço dele. É o que a planta mostra
 * quando o desenho tem contorno; sem contorno, é o que resta, e lê-se de relance.
 */
function GradeDeComodos({
  view,
  aptos,
  unit,
  className,
  selected,
  onToggleRoom,
}: {
  readonly view: FrontRoomsView;
  readonly aptos: readonly AptoDaFrente[];
  readonly unit: string | undefined;
  readonly className?: string | undefined;
  readonly selected?: ReadonlySet<string> | undefined;
  readonly onToggleRoom?: ((room: FrontRoomView) => void) | undefined;
}) {
  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <AvisoDoPeso view={view} />
      {aptos.map((apto) => {
        const avanco = avancoDosComodos(apto.rooms, view.basis);
        const contam = apto.rooms.filter((room) => !room.notApplicable);
        const medidaTotal = contam.reduce((soma, room) => soma + (room.measure ?? 0), 0);
        return (
          <section
            key={apto.positionId}
            aria-label={nomeDoApto(apto.label)}
            className="flex flex-col gap-2.5"
          >
            <h4 className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-sm">
              <span className="font-semibold">{nomeDoApto(apto.label)}</span>
              <span className="font-condensed text-xs text-(--color-content-muted)">
                {apto.unitTypeName}
                {apto.count > 1 && ` · ${String(apto.count)} iguais`}
                {' · '}
                {apto.rooms.length} {apto.rooms.length === 1 ? 'cômodo' : 'cômodos'}
                {medidaTotal > 0 &&
                  ` · ${medidaTotal.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}${unit === undefined ? '' : ` ${unit}`}`}
              </span>
              {avanco !== undefined && (
                <span className="font-condensed ml-auto text-sm font-semibold tabular">
                  {percentual(avanco)}
                </span>
              )}
            </h4>
            <ul
              aria-label={`Cômodos de ${nomeDoApto(apto.label)}`}
              className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-2"
            >
              {apto.rooms.map((room) => (
                <ComodoLadrilho
                  key={chaveDoComodo(room)}
                  room={room}
                  unit={unit}
                  escolhido={selected?.has(chaveDoComodo(room)) === true}
                  onAlternar={onToggleRoom === undefined ? undefined : () => onToggleRoom(room)}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function ComodoLadrilho({
  room,
  unit,
  escolhido = false,
  onAlternar,
}: {
  readonly room: FrontRoomView;
  readonly unit: string | undefined;
  readonly escolhido?: boolean;
  /** Com este, o ladrilho vira botão: clicar põe o cômodo na escolha, ou tira. */
  readonly onAlternar?: (() => void) | undefined;
}) {
  const estado = estadoDoComodo(room);
  const pintado = estado === 'EM_ANDAMENTO' || estado === 'CONCLUIDO';
  const cor = estado === 'CONCLUIDO' ? 'var(--color-state-ok)' : 'var(--color-brand)';
  const percent = Math.min(100, Math.max(0, room.percent ?? 0));
  const medidaDoComodo = medida(room, unit);
  const texto =
    estado === 'NAO_SE_APLICA'
      ? 'não se aplica'
      : room.percent === undefined || room.percent <= 0
        ? ''
        : percentual(room.percent);
  const dica = `${LEGENDA_DO_COMODO[estado]}${room.recordedOn === undefined || estado === 'NAO_SE_APLICA' ? '' : ` · apontado em ${diaEMes(room.recordedOn)}`}`;
  const classe = cn(
    'relative flex min-h-16 flex-col justify-between gap-1.5 overflow-hidden rounded-md border bg-(--color-surface-raised) px-2.5 py-2',
    BORDA_DO_ESTADO[estado],
  );
  const conteudo = (
    <>
      {pintado && (
        <span
          aria-hidden="true"
          data-part="pintura"
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, color-mix(in oklab, ${cor} 24%, transparent) ${String(percent)}%, transparent ${String(percent)}%)`,
          }}
        />
      )}
      <span className="relative text-[13px] leading-tight font-medium">{room.roomName}</span>
      <span className="font-condensed relative flex items-baseline justify-between gap-2 text-xs">
        <span className="text-(--color-content-muted)">{medidaDoComodo}</span>
        <span className="font-semibold tabular">{texto}</span>
      </span>
    </>
  );
  if (onAlternar !== undefined) {
    return (
      <li data-estado={estado} className="flex">
        <button
          type="button"
          aria-pressed={escolhido}
          title={dica}
          onClick={onAlternar}
          className={cn(
            classe,
            'w-full cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-(--color-brand)',
            escolhido && 'outline-2 outline-offset-1 outline-(--color-brand)',
          )}
        >
          {conteudo}
        </button>
      </li>
    );
  }
  return (
    <li data-estado={estado} title={dica} className={classe}>
      {conteudo}
    </li>
  );
}
